import {TextObservable} from "../../../observables/textObservable";
import {NoticeStatus} from "../../../../shared/enums/noticeStatus";
import {BooleanObservable} from "../../../observables/booleanObservable";
import {DateObservable} from "../../../observables/dateObservable";
import {makeAutoObservable, reaction} from "mobx";
import {MultiVillageSelectObservableList} from "../../../observables/multiVillageSelectObservableList";
import {allVillageKeyValue} from "../../../../shared/constants";
import {KeyValue} from "../../../observables/keyValue";
import {addMinutes} from "date-fns";
import {NoticePublishType} from "../../../../shared/enums/noticePublishType";
import {utcToZonedTime} from "date-fns-tz";
import {TimeZones} from "../../../../shared/enums/timeZones";
import {NoticeType} from "../../../../shared/enums/noticeType";
import {ImageEditorObservable} from "../../../observables/imageEditorObservable";
import {containsMarkdownLink} from "../../../../utils/stringUtils";
import {personTypeIds} from "../../../../shared/personTypeIds";

const titleMaxLength = 50;
const authorMaxLength = 80;
const bodyMaxLength = 2500;

export default class NoticeItem {
  id: string;
  title: TextObservable;
  body: TextObservable;
  linksInBodyPolicyChecked: BooleanObservable;
  author: TextObservable;
  status: NoticeStatus;
  expiryDate: DateObservable;
  publishDate: Date | null;
  seenBy: number;
  notificationDate?: Date | null;
  notificationEnabled: BooleanObservable;
  villages: MultiVillageSelectObservableList;
  selectedVillageId?: string | null;
  villageIds?: string[] | null;
  isEditMode: boolean;
  creatorName?: string;
  isMultiVillage: boolean;
  selectedPublishType: NoticePublishType;
  selectedPublishDate: DateObservable;
  timezone: string;
  image: ImageEditorObservable;
  imageCaption: TextObservable;
  heroImagePolicyChecked: BooleanObservable;
  noticeType: TextObservable;
  ignoreQuietHours?: BooleanObservable;
  targetIndependent: BooleanObservable;
  targetServiced: BooleanObservable;

  // A localised version of now, to prevent publish date and notification date being separate
  noticeNow = new Date();

  constructor(
    id: string,
    title: string,
    body: string,
    author: string,
    status: NoticeStatus,
    publishDate: Date | null,
    expiryDate?: Date | null,
    seenBy?: number | null,
    notificationDate?: Date | null,
    notificationEnabled: boolean = false,
    creatorName?: string,
    villageIds?: string[] | undefined,
    isMultiVillage?: boolean | undefined,
    timezone: string = TimeZones.Auckland,
    imageField?: string,
    imageCaption?: string,
    ignoreQuietHours?: boolean | null,
    noticeType: NoticeType = NoticeType.General,
    personTypes?: string[] | null
  ) {
    makeAutoObservable(this);

    this.id = id;
    this.isEditMode = !!id;
    this.creatorName = creatorName;
    this.villageIds = villageIds ?? null;
    this.isMultiVillage = !!isMultiVillage;
    this.villages = new MultiVillageSelectObservableList([], []);
    this.title = new TextObservable(
      title,
      titleMaxLength,
      (value) => value?.trim() !== ""
    );
    this.body = new TextObservable(
      body,
      bodyMaxLength,
      (value) => value?.trim() !== ""
    );
    this.author = new TextObservable(
      author,
      authorMaxLength,
      (value) => value?.trim() !== ""
    );
    this.noticeType = new TextObservable(noticeType);
    this.status = status;
    this.publishDate = publishDate;
    this.timezone = timezone;
    this.expiryDate = new DateObservable(expiryDate, null, (toValidate) => {
      return toValidate !== null;
    });
    this.seenBy = seenBy ?? 0;
    this.notificationDate = notificationDate;
    this.notificationEnabled = new BooleanObservable(notificationEnabled);
    this.ignoreQuietHours = new BooleanObservable(ignoreQuietHours ?? false);
    // New updates default to both audiences; existing updates with no person
    // types stored are independent-only, while an empty set is unassigned.
    const hasAudienceSelected = () =>
      this.targetIndependent.value || this.targetServiced.value;
    this.targetIndependent = new BooleanObservable(
      !this.isEditMode ||
        !personTypes ||
        personTypes.includes(personTypeIds.independent),
      hasAudienceSelected
    );
    this.targetServiced = new BooleanObservable(
      !this.isEditMode ||
        (!!personTypes && personTypes.includes(personTypeIds.serviced)),
      hasAudienceSelected
    );
    this.selectedPublishType =
      this.publishDate && this.publishDate > this.noticeNow
        ? NoticePublishType.Scheduled
        : NoticePublishType.Now;
    this.selectedPublishDate = new DateObservable(
      status === NoticeStatus.Scheduled ? publishDate : null,
      utcToZonedTime(new Date(addMinutes(this.noticeNow, 15)), this.timezone),
      (toValidate) =>
        this.selectedPublishType === NoticePublishType.Now ||
        (toValidate !== null && toValidate > this.noticeNow)
    );

    this.image = new ImageEditorObservable(imageField);
    this.imageCaption = new TextObservable(imageCaption ?? "", 200, (value) => {
      return !(this.image.file && value === "");
    });
    this.heroImagePolicyChecked = new BooleanObservable(
      false,
      (value) => value === true || !this.image.file
    );
    this.linksInBodyPolicyChecked = new BooleanObservable(
      false,
      (value) => value === true || !this.hasLinkInBody
    );

    reaction(
      () => this.noticeType.text,
      (value: string) => {
        if (
          value &&
          (value === NoticeType.Important ||
            value === NoticeType.Announcement) &&
          this.selectedPublishType === NoticePublishType.Now
        ) {
          this.setIsNotificationEnabled(true);
        }
      }
    );
    // Clearing the image caption if image is removed.
    reaction(
      () => this.image.file,
      (value) => {
        if (!value) {
          this.imageCaption.text = "";
        }
      }
    );
  }

  get hasChanges() {
    return (
      this.title.isDirty ||
      this.body.isDirty ||
      this.author.isDirty ||
      this.expiryDate.isDirty ||
      this.notificationEnabled.isDirty ||
      this.villages.isDirty ||
      this.selectedPublishDate.isDirty ||
      this.image.isDirty ||
      this.imageCaption.isDirty ||
      this.noticeType.isDirty ||
      this.heroImagePolicyChecked.isDirty ||
      this.linksInBodyPolicyChecked.isDirty ||
      this.targetIndependent.isDirty ||
      this.targetServiced.isDirty
    );
  }

  get canPublish(): boolean {
    let isMultiVillageSelectionValid = true;
    if (this.isMultiVillage) {
      isMultiVillageSelectionValid = this.villages.isValid;
    }
    return (
      this.hasChanges &&
      this.isValid &&
      isMultiVillageSelectionValid &&
      this.isPersonTypeSelectionValid
    );
  }

  get canSaveDraft(): boolean {
    // Can "Save Draft" when only 0 or more than 1 village are selected
    // Cannot "Save Draft" when only 1 village is selected (this would make it a single village update)
    let isMultiVillageSelectionValid = true;
    if (this.isMultiVillage) {
      isMultiVillageSelectionValid =
        this.villages.selectedItems.length === 0 ||
        this.villages.selectedItems.length > 1 ||
        this.villages.allVillageOptionSelected();
    }

    return (
      this.title.isLengthValid() &&
      this.body.isLengthValid() &&
      this.author.isLengthValid() &&
      isMultiVillageSelectionValid &&
      this.image.isValid &&
      this.imageCaption.isLengthValid()
    );
  }

  get isValid(): boolean {
    return (
      this.title.isValid &&
      this.body.isValid &&
      this.author.isValid &&
      this.expiryDate.isValid &&
      this.selectedPublishDate.isValid &&
      this.image.isValid &&
      this.imageCaption.isValid &&
      this.heroImagePolicyChecked.isValid &&
      this.linksInBodyPolicyChecked.isValid
    );
  }

  get isImportant(): boolean {
    return this.noticeType.text === NoticeType.Important;
  }

  get isAnnouncement(): boolean {
    return this.noticeType.text === NoticeType.Announcement;
  }

  get isPersonTypeSelectionValid(): boolean {
    return this.targetIndependent.isValid && this.targetServiced.isValid;
  }

  get personTypes(): string[] {
    const selected: string[] = [];
    if (this.targetIndependent.value) {
      selected.push(personTypeIds.independent);
    }
    if (this.targetServiced.value) {
      selected.push(personTypeIds.serviced);
    }
    return selected;
  }

  setIsDirty = (value: boolean) => {
    this.title.setIsDirty(value);
    this.body.setIsDirty(value);
    this.author.setIsDirty(value);
    this.expiryDate.setIsDirty(value);
    this.villages.setIsDirty(value);
    this.selectedPublishDate.setIsDirty(value);
    this.image.setIsDirty(value);
    this.imageCaption.setIsDirty(value);
    this.heroImagePolicyChecked.setIsDirty(value);
    this.linksInBodyPolicyChecked.setIsDirty(value);
    this.targetIndependent.setIsDirty(value);
    this.targetServiced.setIsDirty(value);
  };

  private getRequestPublishDate = () => {
    const isPublishingAScheduledNoticeNow =
      this.selectedPublishType === NoticePublishType.Now &&
      this.publishDate &&
      this.publishDate > this.noticeNow;
    if (this.selectedPublishType === NoticePublishType.Scheduled) {
      return this.selectedPublishDate.date;
    } else if (isPublishingAScheduledNoticeNow) {
      return this.noticeNow;
    }
    return this.publishDate || this.noticeNow;
  };

  get requestObject() {
    if (!this.isMultiVillage) {
      this.villageIds = [this.selectedVillageId!];
    } else if (
      this.villages.allVillageOptionSelected() ||
      this.villages.selectedItems.length === 0
    ) {
      this.villageIds =
        this.villages.selectedItems.length === 0
          ? [] // Empty is used when user is saving a draft notice without selecting any villages.
          : null; // null is used when 'All village' option is selected by user
    } else {
      this.villageIds = this.villages.selectedItems.map((value) => value.value);
    }

    return {
      id: this.id,
      title: this.title.text.trim(),
      body: this.body.text,
      author: this.author.text.trim(),
      isImportant: this.isImportant,
      isAnnouncement: this.isAnnouncement,
      status: this.status,
      publishDate:
        this.status === NoticeStatus.Draft
          ? null
          : this.getRequestPublishDate(),
      expiryDate: this.expiryDate.date !== null ? this.expiryDate.date : null,
      notificationDate: this.notificationDate ? this.notificationDate : null,
      villageIds: this.villageIds,
      personTypes: this.personTypes,
      fileId: this.image.fileId,
      imageDescription: this.imageCaption.text,
      ignoreQuietHours: this.ignoreQuietHours?.value
    };
  }

  setMultiVillageNotice() {
    if (!this.isMultiVillage) return;

    if (!this.villageIds) {
      this.villages.setItems([allVillageKeyValue]);
    } else {
      let keyValues = this.villageIds.map(
        (value) => new KeyValue(value, "N/A")
      );
      this.villages.setSelectedItems(keyValues);
    }
  }

  setIsNotificationEnabled = (value: boolean) => {
    this.notificationEnabled.setBoolean(value);
  };

  setHeroImagePolicyChecked = (value: boolean) => {
    this.heroImagePolicyChecked.setBoolean(value);
  };

  setLinksInBodyPolicyChecked = (value: boolean) => {
    this.linksInBodyPolicyChecked.setBoolean(value);
  };

  setTargetIndependent = (value: boolean) => {
    this.targetIndependent.setBoolean(value);
  };

  setTargetServiced = (value: boolean) => {
    this.targetServiced.setBoolean(value);
  };

  get hasLinkInBody() {
    return containsMarkdownLink(this.body.text);
  }
}
